// Constants
import { TaskGroupingState } from "@ms/uno-constants/lib/local/GroupingConstants";
import { DnDRoleAttribute, ItemTypes } from "@ms/uno-constants/lib/local/DndConstants";
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { TaskCard } from "../../taskCard/TaskCard";
import { VirtualContainer } from "../../../virtualization/VirtualContainer";
import { getILabelArray } from "@ms/uno-models/lib/local/utilities/LabelUtilities";
import { Assignment } from "@ms/uno-models/lib/local/client/legacyPlanner/task/Assignment";
import { ConvertPercentCompleteToProgressState } from "@ms/uno-models/lib/local/client/legacyPlanner/utilities/ConversionUtilities";
import { ProgressState } from "@ms/uno-models/lib/local/client/legacyPlanner/progressState/ProgressState";
// Styles
import { styled, classNamesFunction } from "@fluentui/react";
import { getStyles } from "./TaskBoardCard.styles";
// Utilities
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
/** Callback to generate classnames for the component styles */ const getClassNames = classNamesFunction();
/** Base component to be wrapped with `styled` */ const TaskBoardCardBase = (props)=>{
    const { taskBoardCardCallbacks, maxAssignees, maxDisplayablePersonas, showPreview, showPlanTitle, showCopyLinkToTask, currentUserId, taskPermissions, canAddGuests, styles, onDelete, onTaskCardClick, onKeyboardTaskReorder, restoreFocusOnMoveTask, setRef, onCardDragStateChange, index, entity, columnValue, secondaryGrouping, taskBoardCardPositionInColumnGroup, totalTasksInColumnGroup, availableAgents, agentsInfoContentMap, isDragPreview } = props;
    /** Reference to a task card complete button */ const completeBtnRef = useRef(null);
    /** Reference to a task card more button */ const moreBtnRef = useRef(null);
    /** Reference to a component */ const taskBoardCardRef = useRef(null);
    /** Reference to the last key down */ const lastKeyDownRef = useRef(null);
    //#region State
    /** Indicates if FocusZone is enabled */ const [enableFocusZone, setEnableFocusZone] = useState(false);
    //#endregion
    //#region Drag and Drop
    /**
     * Handle item being dropped onto this card
     * @param draggedItem The item being dropped
     */ const onDrop = useCallback((item)=>{
        // Add dragged assignee to the list of assignees
        if (entity.getAssigneeIds().indexOf(item.userId) === -1) {
            if (maxAssignees && entity.getAssigneeIds().length >= maxAssignees) {
                taskBoardCardCallbacks.showAssigneeLimitError(entity.id);
                return;
            }
            const updatedTask = entity.getClone();
            updatedTask.assignments[item.userId] = new Assignment(currentUserId, new Date());
            taskBoardCardCallbacks.taskCallbacks.onUpdateTask(entity, updatedTask);
        }
    }, [
        entity,
        maxAssignees,
        currentUserId,
        taskBoardCardCallbacks
    ]);
    const propsRef = useRef(props);
    propsRef.current = props;
    // task card as a drag source
    const [{ isDragging }, dragRef] = useDrag(()=>{
        return {
            type: ItemTypes.Card,
            item: ()=>({
                    entity: entity,
                    sourceColumnValue: columnValue,
                    secondaryGrouping: secondaryGrouping,
                    index: index,
                    taskPermissions: taskPermissions,
                    cardProps: propsRef.current
                }),
            collect: (monitor)=>({
                    isDragging: monitor.isDragging()
                }),
            // Task can't be dragged unless have edit perms
            canDrag: ()=>taskPermissions.canEdit
        };
    }, [
        entity,
        columnValue,
        secondaryGrouping,
        index,
        taskPermissions
    ]);
    // task card as a drop target
    const [{ isOver }, dropRef] = useDrop(()=>({
            accept: ItemTypes.MemberCard,
            collect: (monitor)=>({
                    isOver: monitor.isOver()
                }),
            drop: onDrop
        }), [
        onDrop
    ]);
    // this is to support both drag and drop on the same element
    const dndRef = useCallback((element)=>{
        dragRef(element);
        dropRef(element);
    }, [
        dragRef,
        dropRef
    ]);
    //#endregion
    //#region callbacks
    /**
     * Handle click on the task card
     */ const onCardClick = useCallback(()=>{
        onTaskCardClick(entity.id);
    }, [
        entity.id,
        onTaskCardClick
    ]);
    /**
     * Called on key down on the task card
     * @param event Describes a user interaction with the keyboard
     */ const handleOnKeyDown = useCallback((event)=>{
        event.stopPropagation();
        // Make sure the keyDown event is triggered by a key down on the task card itself
        if (event.target === event.currentTarget) {
            lastKeyDownRef.current = event.keyCode;
        }
    }, []);
    /**
     * Called on key up on the task card
     * @param event Describes a user interaction with the keyboard
     */ const handleOnKeyUp = useCallback((event)=>{
        event.stopPropagation();
        const lastKeyDown = lastKeyDownRef.current;
        // Make sure the key press began on this element
        if (event.keyCode === lastKeyDown) {
            if (event.keyCode === KeyCode.DOWN_ARROW && event.ctrlKey && taskPermissions.canEdit) {
                if (onKeyboardTaskReorder) {
                    onKeyboardTaskReorder(columnValue, entity, false);
                }
            } else if (event.keyCode === KeyCode.UP_ARROW && event.ctrlKey && taskPermissions.canEdit) {
                if (onKeyboardTaskReorder) {
                    onKeyboardTaskReorder(columnValue, entity, true);
                }
            } else if (event.keyCode === KeyCode.SPACE || event.keyCode === KeyCode.ENTER) {
                onTaskCardClick(entity.id);
            } else if (event.keyCode === KeyCode.RIGHT_ARROW) {
                event.preventDefault();
                setEnableFocusZone(true);
            }
        }
        lastKeyDownRef.current = null;
    }, [
        columnValue,
        entity,
        onKeyboardTaskReorder,
        onTaskCardClick,
        taskPermissions.canEdit
    ]);
    /**
     * Helper function to set the focus on this task board card
     */ const setFocusOnCard = useCallback(()=>{
        taskBoardCardRef.current?.focus();
    }, []);
    /**
     * A callback method that disables the focuszone when user tabs away from the actionable task card items
     */ const disableFocusZone = useCallback(()=>{
        setEnableFocusZone(false);
    }, []);
    /**
     * Modified onUpdateTask callback to handle refocusing cases
     * @param originalTask The original task to be updated
     * @param updatedTask The updated task
     */ const onUpdateTask = useCallback((originalTask, updatedTask)=>{
        // On progress changes from taskcard, handle refocusing
        if (originalTask.percentComplete !== updatedTask.percentComplete) {
            switch(ConvertPercentCompleteToProgressState(updatedTask.percentComplete)){
                case ProgressState.NotStarted:
                    if ((taskBoardCardCallbacks.getCurrentTaskGroupingState() === TaskGroupingState.Progress || ConvertPercentCompleteToProgressState(originalTask.percentComplete) === ProgressState.Complete) && restoreFocusOnMoveTask != null) {
                        // If the progress is changed to not started when tasks are grouped by progress, set focus on the next in progress task
                        restoreFocusOnMoveTask(updatedTask.id);
                    } else {
                        // Otherwise, keep focus on the current task card
                        setFocusOnCard();
                    }
                    break;
                case ProgressState.InProgress:
                    break;
                case ProgressState.Complete:
                    if (restoreFocusOnMoveTask != null) {
                        restoreFocusOnMoveTask(updatedTask.id);
                    }
                    break;
                default:
                    return;
            }
        }
        taskBoardCardCallbacks.taskCallbacks.onUpdateTask(originalTask, updatedTask);
    }, [
        taskBoardCardCallbacks,
        restoreFocusOnMoveTask,
        setFocusOnCard
    ]);
    const taskCardCallbacks = useMemo(()=>{
        return {
            ...taskBoardCardCallbacks.taskCallbacks,
            onUpdateTask: onUpdateTask
        };
    }, [
        taskBoardCardCallbacks.taskCallbacks,
        onUpdateTask
    ]);
    /**
     * Ref callback to set the task card element reference.
     * The preview clone must not register as a real drag/drop source/target or report its DOM node
     * back to the parent under the real card's id, so both are skipped when isDragPreview is true.
     */ const onTaskCardSetRef = useCallback((element)=>{
        taskBoardCardRef.current = element;
        if (isDragPreview) {
            return;
        }
        setRef(element, entity.id);
        dndRef(element);
    }, [
        dndRef,
        entity.id,
        setRef,
        isDragPreview
    ]);
    /** Callback to fetch the plan details */ const fetchPlanDetailsOnLoad = ()=>{
        if (isDragPreview) {
            return;
        }
        if (entity.planId) {
            taskBoardCardCallbacks.fetchPlanData?.(entity.planId);
        }
    };
    //#endregion
    //#region Render
    const classNames = getClassNames(styles, {
        isDragging
    });
    const renderTaskCard = ()=>{
        // The card should be highlighted with assignee format when a drag source item is over it
        // Or when the assigneeHighlighted is true
        const highlightTypeForTask = taskBoardCardCallbacks.getTaskHighlightedType(entity);
        if (isOver) {
            highlightTypeForTask.assigneeHighlighted = true;
        }
        let labels = [];
        const planDetails = entity.planId != null ? taskBoardCardCallbacks.getTaskPlanDetails(entity.planId) : null;
        const labelUsageCounts = taskBoardCardCallbacks.getTaskPlanLabelUsageCounts(entity.planId);
        labels = getILabelArray(planDetails?.categories || [], labelUsageCounts);
        const taskCard = /*#__PURE__*/ React.createElement(TaskCard, {
            key: entity.id,
            task: entity,
            taskBucket: entity.bucketId != null ? taskBoardCardCallbacks.getTaskBucket(entity.bucketId) : null,
            taskDetails: taskBoardCardCallbacks.getTaskDetails(entity.id),
            taskPlan: entity.planId != null ? taskBoardCardCallbacks.getTaskPlan(entity.planId) : null,
            taskPlanLabels: labels,
            callbacks: taskCardCallbacks,
            showBucketTitle: !showPlanTitle && taskBoardCardCallbacks.getCurrentTaskGroupingState() !== TaskGroupingState.Bucket,
            showPlanTitle: showPlanTitle,
            showPreview: showPreview,
            showCopyLinkToTask: showCopyLinkToTask,
            highlightType: highlightTypeForTask,
            isFocusZoneEnabled: enableFocusZone,
            maxAssignees: maxAssignees,
            onDelete: onDelete,
            onFocusContainer: setFocusOnCard,
            onTaskCardClick: onTaskCardClick,
            completeBtnRef: completeBtnRef,
            moreBtnRef: moreBtnRef,
            disableFocusZone: disableFocusZone,
            currentUserId: currentUserId,
            taskPermissions: taskPermissions,
            canAddGuests: canAddGuests,
            maxDisplayablePersonas: maxDisplayablePersonas,
            availableAgents: availableAgents,
            isAITask: taskBoardCardCallbacks.isAITask(entity.id),
            agentsInfoContentMap: agentsInfoContentMap
        });
        return taskBoardCardCallbacks.fetchPlanData ? /*#__PURE__*/ React.createElement(VirtualContainer, {
            containerId: entity.planId,
            onLoad: fetchPlanDetailsOnLoad
        }, taskCard) : taskCard;
    };
    //#endregion
    //#region Effects
    useEffect(()=>{
        // Skip reporting drag state from the preview clone: it never registers as a real drag source, so
        // its own isDragging stays false and would otherwise race with the real card's true/false reports.
        if (isDragPreview) {
            return;
        }
        // isDragging comes from useDrag hook, report the drag state to the parent using the callback
        onCardDragStateChange?.(isDragging);
    }, [
        isDragging,
        onCardDragStateChange,
        isDragPreview
    ]);
    //#endregion
    return /*#__PURE__*/ React.createElement("div", {
        key: entity.id,
        id: entity.id,
        className: classNames.root,
        "data-index": index,
        "aria-label": entity.title,
        "aria-posinset": taskBoardCardPositionInColumnGroup + 1,
        "aria-setsize": totalTasksInColumnGroup,
        "data-dnd-role": DnDRoleAttribute.card,
        "data-is-focusable": !isDragPreview,
        role: "listitem",
        onClick: isDragPreview ? undefined : onCardClick,
        tabIndex: isDragPreview ? -1 : 0,
        "aria-hidden": isDragPreview || undefined,
        onKeyDown: isDragPreview ? undefined : handleOnKeyDown,
        onKeyUp: isDragPreview ? undefined : handleOnKeyUp,
        ref: onTaskCardSetRef
    }, renderTaskCard());
};
/**
 * TaskBoardCard component that contains a task card
 */ export const TaskBoardCard = styled(TaskBoardCardBase, getStyles);
